import { useCallback, useEffect, useMemo, useState } from 'react';
import { Api } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const ORDER_FILTERS = [
  { value: 'pending', labelKey: 'orders.filter.pending' },
  { value: 'completed', labelKey: 'orders.filter.completed' },
];

const OrdersPage = () => {
  const [activeFilter, setActiveFilter] = useState(ORDER_FILTERS[0].value);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const meta = JSON.parse(window.localStorage.getItem('myUser'));
      setCustomerId(meta?.customer_id || null);
    } catch {
      setCustomerId(null);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!customerId) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response =
        activeFilter === 'pending'
          ? await Api.fetchPendingOrders(customerId)
          : await Api.fetchOrdersByStatus(customerId, '6');
      const payload = response?.data?.data || response?.data || [];
      setOrders(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setOrders([]);
      setError(err?.message || 'Unable to load your orders right now.');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, customerId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (!customerId) return undefined;
    const id = setInterval(() => fetchOrders(), 20000);
    return () => clearInterval(id);
  }, [customerId, fetchOrders]);

  const emptyMessage = useMemo(() => {
    return activeFilter === 'pending'
      ? t('orders.empty_pending')
      : t('orders.empty_completed');
  }, [activeFilter, t]);

  const getStatusLabel = (order) => {
    if (!order?.orderStatus?.name) {
      return activeFilter === 'pending'
        ? t('orders.status.pending')
        : t('orders.status.completed');
    }
    return order.orderStatus.name;
  };

  const handleRowClick = (order) => {
    const orderId = order?.orderId || order?.id || order?.orderNumber;
    if (!orderId) return;
    try {
      window.sessionStorage.setItem(
        'selectedOrderData',
        JSON.stringify(order)
      );
    } catch {
      // ignore storage errors
    }
    const search = `?id=${encodeURIComponent(orderId)}`;
    window.location.assign(`/order-details${search}`);
  };

  return (
    <div className="orders-history">
      <header className="orders-history__header">
        <h1>{t('orders.title')}</h1>
        <div className="orders-history__filter">
          <label>
            <span>{t('orders.columns.status')}</span>
            <select
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
            >
              {ORDER_FILTERS.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {t(filter.labelKey)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <div className="orders-table">
        <div className="orders-table__head">
          <span>{t('orders.columns.id')}</span>
          <span>{t('orders.columns.date')}</span>
          <span>{t('orders.columns.status')}</span>
          <span>{t('orders.columns.price')}</span>
        </div>
        {loading ? (
          <div className="orders-table__state">
            <p>{t('orders.loading')}</p>
          </div>
        ) : error ? (
          <div className="orders-table__state">
            <p>{error}</p>
          </div>
        ) : !orders.length ? (
          <div className="orders-table__state">
            <p>{emptyMessage}</p>
          </div>
        ) : (
          orders.map((order) => {
            const orderId = order.orderNumber || order.orderId || order.id;
            return (
              <button
                type="button"
                key={orderId}
                className="orders-table__row"
                onClick={() => handleRowClick(order)}
              >
                <span>#{orderId}</span>
                <span>{formatDate(order.date || order.dateCreated)}</span>
                <span>{getStatusLabel(order)}</span>
                <span>{formatCurrency(order.totalAmount || order.total)}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
